// import * as dotenv from 'dotenv';
import axios from "axios";
import axiosInterceptors from "./axiosHandler";

// API origin. REACT_APP_API_URL (set at build time on Amplify) wins; otherwise
// localhost in development and the production API.
//
// SHARED LOGIN NOTE: the API sets a session cookie scoped to .importjdmmagic.com
// on login so the marketing site (www.importjdmmagic.com) and this portal share
// one login. A browser only accepts that cookie when the response comes from a
// host under importjdmmagic.com - so the cross-site session only lights up once
// this points at https://api.importjdmmagic.com (same box as api.glojapan.com,
// needs a DNS record + nginx server_name + cert). Until then everything here
// keeps working exactly as before against api.glojapan.com; the cookie is just
// ignored by the browser.
export const baseURL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:5002"
    : "https://api.glojapan.com");

// Add a new function to force refresh the user's verification status
export const refreshUserVerification = async () => {
  try {
    const response = await getUser();
    if (response.status === 200 && response.data) {
      // Extract user data from the response, checking for different possible structures
      let userData = null;
      
      // Direct user data (checking for userId instead of _id)
      if (response.data.userId || response.data._id) {
        userData = response.data;
      }
      // Nested structure: {message, data, status} where user data is in the 'data' property
      else if (response.data.data && typeof response.data.data === 'object' && (response.data.data.userId || response.data.data._id)) {
        userData = response.data.data;
      }
      
      if (userData) {
        // Only a "Remember for 30 days" sign-in keeps the record past this tab.
        if (localStorage.getItem('token')) {
          localStorage.setItem('userData', JSON.stringify(userData));
        }
        sessionStorage.setItem('userData', JSON.stringify(userData));
        return userData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error refreshing user verification status:', error);
    return null;
  }
};

// ---- Shared-session (www <-> portal) bootstrap -------------------------------
//
// Plain axios on purpose, NOT axiosInterceptors(): the interceptor treats a 401
// as "session expired" and reloads the page onto /login, which is exactly wrong
// for a probe whose normal answer for a signed-out visitor is 401.

// GET /auth/session with the shared cookie only. Resolves to
// { status, data } where data is the login-shaped body ({ token, userId,
// firstName, ... }) on 200 and null otherwise. The status is kept so callers
// can tell a definitive "no session" (401) from a network failure (0): only
// the former means the user signed out on the other site.
export const getSession = async () => {
  try {
    const res = await axios.get(baseURL + "/auth/session", {
      withCredentials: true,
      validateStatus: () => true,
    });
    if (res.status === 200 && res.data?.data?.token) {
      return { status: 200, data: res.data.data };
    }
    return { status: res.status, data: null };
  } catch (error) {
    // Network failure or CORS refusal: never throw.
    return { status: 0, data: null };
  }
};

// POST /auth/logout - drops the shared cookie so the marketing site is signed
// out too. Best effort; local storage is cleared regardless.
export const logoutSession = async () => {
  try {
    await axios.post(baseURL + "/auth/logout", null, {
      withCredentials: true,
      validateStatus: () => true,
    });
  } catch (error) {
    // ignore - nothing to do if the API is unreachable
  }
};

export const carSearch = (data) => {
  return axiosInterceptors()
    .get(baseURL + "/cars/search?" + data)
    .then((res) => res);
};

export const getCarDetails = (data) => {
  console.log("Making API call to get car details with payload:", data);
  return axiosInterceptors()
    .post(baseURL + "/cars/detail", data)
    .then((res) => {
      console.log("API response for car details:", res.status, res.data?.message);
      return res;
    });
};

export const getCarFilters = (data) => {
  return axiosInterceptors()
    .get(baseURL + "/cars/filters?" + data)
    .then((res) => res);
};

export const getCarMarketStats = (params) => {
  const query = new URLSearchParams(params).toString();
  return axiosInterceptors()
    .get(baseURL + "/cars/market-stats?" + query)
    .then((res) => res);
};

// Earlier auctions of the same car, matched on the chassis number read off
// the auction sheets. Resolves with the body: { data: { status, ... } }.
export const getAuctionHistory = (carId) => {
  return axiosInterceptors().get(baseURL + "/cars/auction-history/" + encodeURIComponent(carId));
};

export const getProductionDate = (params) => {
  const query = new URLSearchParams(params).toString();
  return axiosInterceptors().get(baseURL + "/cars/production-date?" + query);
};

export const getTranslatedAuctionSheet = (data) => {
  return axiosInterceptors().post(
    baseURL + "/cars/getTranslatedAuctionSheet",
    data
  );
};

export const checkDuplicates = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/check-duplicates", data)
    .then((res) => res);
};

export const signup = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/signup", data)
    .then((res) => res);
};

export const signin = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/login", data)
    .then((res) => res);
};

export const getUser = async () => { 
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token) {
    return { status: 401, message: "No authentication token found" };
  }
  
  try {
    const response = await axiosInterceptors()
      .get(baseURL + "/auth/me", {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then((res) => {
        console.log("Auth API Response:", res);
        // Log the response structure to help debug
        if (res.data) {
          console.log("Response data structure:", {
            hasData: !!res.data,
            dataType: typeof res.data,
            dataKeys: res.data ? Object.keys(res.data) : [],
            hasNestedData: !!res.data?.data,
            nestedDataType: res.data?.data ? typeof res.data.data : 'undefined',
            nestedDataKeys: res.data?.data ? Object.keys(res.data.data) : []
          });
        }
        return res;
      });
    return response;
  } catch (error) {
    console.error("Error in getUser:", error);
    return { status: 500, message: "Failed to fetch user data" };
  }
}

export const verifyUser = async (data) => { 
  return axiosInterceptors()
    .post(baseURL + "/auth/upgrade-tier", data)
    .then((res) => res);
}

export const getUsers = async (data) => {
  console.log("DEBUG - Making API call to get users with query:", data);
  try {
    const response = await axiosInterceptors().get(baseURL + "/users?" + (data || ''));
    console.log("DEBUG - Users API response:", response);
    
    // Handle different response structures
    if (response?.data?.data && Array.isArray(response.data.data)) {
      console.log("DEBUG - Found users array in response.data.data", response.data.data.length);
      return { status: response.status, message: "Success", data: response.data.data };
    } else if (Array.isArray(response?.data)) {
      console.log("DEBUG - Found users array in response.data", response.data.length);
      return { status: response.status, message: "Success", data: response.data };
    } else {
      console.log("DEBUG - Unknown response structure:", response);
      return { status: response.status || 200, message: response?.message || "Unknown response format", data: [] };
    }
  } catch (error) {
    console.error("DEBUG - Error fetching users:", error);
    console.error("DEBUG - Error details:", error.response?.data || error.message);
    // Return a structured error response
    return { 
      status: error.response?.status || 500, 
      data: [], 
      message: "Error fetching users"
    };
  }
}

// The reset link host is chosen by the server. Never send a `domain` (or any other
// host hint) from the browser — it decides where the reset email points.
export const forgotPassword = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/users/forgot-password", data)
    .then((res) => res);
};

export const resetPassword = async (data,token) => {
  return axiosInterceptors()
    .post(baseURL + `/users/forgot-password/${token}`,data)
    .then((res) => res);
};

// POST /auth/verify-otp — body is { email, otp }. The route is unauthenticated: it
// matches the otp against server side state keyed by email. On success it answers
// { data: { token } } where `token` is a 15 minute, single use PASSWORD RESET token.
// It is not a session: only ever pass it as the :token segment of
// /users/forgot-password/:token, never store it as the login token.
export const verifyOTP = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/verify-otp", data)
    .then((res) => res);
};

export const changePassword = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/users/change-password", data)
    .then((res) => res);
};

export const updateProfile = async (data) => {
  return axiosInterceptors()
    .put(baseURL + "/users", data)
    .then((res) => res);
};

export const updateUserImage = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/users/upload", data)
    .then((res) => res);
}


export const updateUser = async (data,userId) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/user", data , {params: {userId: userId}})
    .then((res) => res);
};

export const updateUserData = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/update", data)
    .then((res) => res);
};

export const updateCarFavourite = async (data) => {
  // Handle both cases: either a simple ID string or a data object
  const payload = typeof data === 'string' || data instanceof String 
    ? { carId: data } // If it's just a string ID, wrap it as {carId: id}
    : data;           // Otherwise use the data object as is (for backward compatibility)
  
  return axiosInterceptors()
    .post(baseURL + "/cars/favorite", payload)
    .then((res) => res);
};

export const getCarFavourites = async (data) => {
  return axiosInterceptors()
    .get(baseURL + "/cars/favorite?" + data)
    .then((res) => res);
};

export const getTrackingDetail = async (data) => {
  return axiosInterceptors()
    .get(baseURL + `/track/${data}`)
    .then((res) => res);
}

export const sendInspectionRequest = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/email/sendInspectionRequest", data)
    .then((res) => res);
};

// Registration email verification. The OTP is generated, stored and checked entirely
// on the server — the browser never sees it and must never generate one.
export const sendEmailVerificationOtp = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/email-otp/send", data)
    .then((res) => res);
};

// Returns { data: { emailVerificationToken } } on success. Keep that token in
// component state only (it is proof of email ownership, not a session) and pass it
// straight to signup.
export const verifyEmailVerificationOtp = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/email-otp/verify", data)
    .then((res) => res);
};

export const sendPhoneOtp = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/sms/sendOtp", data)
    .then((res) => res);
};

export const verifyPhoneOtp = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/sms/verifyOtp", data)
    .then((res) => res);
};

export const requestInspection = async (carId) => {
  return axiosInterceptors()
    .post(baseURL + "/cars/inspection/user", { carId })
    .then((res) => res);
}

export const getUserInspections = async (carId) => {
  return axiosInterceptors()
    .get(baseURL + "/cars/inspection/user", {params: {carId: carId}})
    .then((res) => res);
}

export const getInspections = async () => {
  return axiosInterceptors()
    .get(baseURL + "/cars/inspection")
    .then((res) => res);
}

export const updateInspectionRequest = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/cars/inspection", data)
    .then((res) => res);
}

// One status for many inspections; the server emails each customer once.
export const bulkUpdateInspectionStatus = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/cars/inspection/bulk", data)
    .then((res) => res);
}

export const uploadInspectionImages = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/cars/inspection/upload/", data)
    .then((res) => res);
}

export const getUserPurchases = async (userId) => {
  return axiosInterceptors()
    .get(baseURL + "/purchase", { params: { userId } })
    .then((res) => res);
}

export const getAllPurchases = async () => {
  return axiosInterceptors()
    .get(baseURL + "/purchase/list")
    .then((res) => res);
}

export const createUserPurchase = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/purchase", data)
    .then((res) => res);
}

export const getNotifications = async () => {
  return axiosInterceptors()
    .get(baseURL + "/notification")
    .then((res) => res);
}

export const createUserNotification = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/notification", data)
    .then((res) => res);
}

export const updateNotifications = async (notificationId) => {
  return axiosInterceptors()
    .put(baseURL + "/notification",notificationId)
    .then((res) => res);
}

export const updateUserPurchase = async (data,purchaseId,userId) => {
  return axiosInterceptors()
    .put(baseURL + "/purchase", data ,  { params: { purchaseId, userId } })
    .then((res) => res);
}

export const updateUserPurchaseStatus = async (data,purchaseId,userId) => {
  return axiosInterceptors()
    .post(baseURL + "/purchase/status", data ,  { params: {  purchaseId,userId } })
    .then((res) => res);
}

// Bid related API functions
export const createBid = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/cars/bid", data)
    .then((res) => res);
}

export const getUserBids = async (carId) => {
  let queryParams = carId ? `?carId=${carId}` : '';
  return axiosInterceptors()
    .get(baseURL + "/cars/bid/user" + queryParams)
    .then((res) => res);
}

export const updateBidStatus = async (data) => {
  return axiosInterceptors()
    .put(baseURL + "/cars/bid/status", data)
    .then((res) => res);
}

// Body is { bidId } only. The server loads the bid, checks the caller owns it and
// builds the admin email from the stored record — nothing else sent here is read.
export const sendBidNotification = async (data) => {
  return axiosInterceptors()
    .post(baseURL + "/email/sendBidNotification", data)
    .then((res) => res);
}

export const updateUserDeposit = async (data, userId) => {
  return axiosInterceptors()
    .post(baseURL + "/auth/update-deposit?userId=" + userId, data)
    .then((res) => res);
};

// Analytics API functions
export const getAnalyticsDashboard = async (days = 30) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/dashboard?days=${days}`)
    .then((res) => res);
};

export const getMostActiveUsers = async (days = 30, limit = 20) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/active-users?days=${days}&limit=${limit}`)
    .then((res) => res);
};

export const getPopularCars = async (days = 30, limit = 20) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/popular-cars?days=${days}&limit=${limit}`)
    .then((res) => res);
};

export const getPopularSearches = async (days = 30, limit = 20) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/popular-searches?days=${days}&limit=${limit}`)
    .then((res) => res);
};

export const getPopularListings = async (days = 7, limit = 20) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/popular-listings?days=${days}&limit=${limit}`)
    .then((res) => res);
};

export const getRecentActivity = async (limit = 50) => {
  return axiosInterceptors()
    .get(baseURL + `/analytics/recent-activity?limit=${limit}`)
    .then((res) => res);
};